import api from './api';

/**
 * Image service for handling menu item image operations
 * Provides functions for uploading and deleting menu item images
 */

export interface ImageUploadResponse {
  success: boolean;
  data: {
    publicId: string;
    url: string;
    menuItemId: string;
    size: number;
  };
}

export interface ImageDeleteResponse {
  success: boolean;
  data: {
    success: boolean;
    publicId: string;
    message: string;
  };
}

export interface MenuImageUploadResponse {
  success: boolean;
  data: {
    publicId: string;
    url: string;
    menuId: string;
    size: number;
  };
}

export interface MenuImageDeleteResponse {
  success: boolean;
  data: {
    success: boolean;
    publicId: string;
    message: string;
    menuId: string;
  };
}

export interface OnboardingModalImageUploadResponse {
  success: boolean;
  data: {
    publicId: string;
    url: string;
    menuId: string;
    size: number;
  };
}

export interface OnboardingModalImageDeleteResponse {
  success: boolean;
  data: {
    success: boolean;
    publicId: string;
    message: string;
    menuId: string;
  };
}

export interface CategoryTopImageUploadResponse {
  success: boolean;
  data: {
    publicId: string;
    url: string;
    menuId: string;
    size: number;
  };
}

export interface CategoryTopImageDeleteResponse {
  success: boolean;
  data: {
    success: boolean;
    publicId: string;
    message: string;
    menuId: string;
  };
}

/**
 * Upload an image for a menu item
 * @param menuItemId - The ID of the menu item
 * @param imageFile - The image file to upload
 * @returns Promise with upload result
 */
export const uploadMenuItemImage = async (
  menuItemId: string,
  imageFile: File
): Promise<ImageUploadResponse> => {
  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('menuItemId', menuItemId);

  const response = await api.post('/upload/cloudinary/menu-item', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Upload an image for a menu
 * @param menuId - The ID of the menu
 * @param imageFile - The image file to upload
 * @returns Promise with upload result
 */
export const uploadMenuImage = async (
  menuId: string,
  imageFile: File
): Promise<MenuImageUploadResponse> => {
  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('menuId', menuId);

  const response = await api.post('/upload/cloudinary/menu-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Delete an image for a menu item
 * @param menuItemId - The ID of the menu item
 * @returns Promise with delete result
 */
export const deleteMenuItemImage = async (
  menuItemId: string
): Promise<ImageDeleteResponse> => {
  const response = await api.delete(`/upload/cloudinary/menu-item/${menuItemId}`);
  return response.data;
};

/**
 * Delete a menu image
 * @param menuId - The ID of the menu
 * @returns Promise with delete result
 */
export const deleteMenuImage = async (
  menuId: string
): Promise<MenuImageDeleteResponse> => {
  const response = await api.delete(`/upload/cloudinary/menu-image/${menuId}`);
  return response.data;
};

/**
 * Upload an onboarding modal image for a menu
 * @param menuId - The ID of the menu
 * @param imageFile - The image file to upload
 * @returns Promise with upload result
 */
export const uploadOnboardingModalImage = async (
  menuId: string,
  imageFile: File
): Promise<OnboardingModalImageUploadResponse> => {
  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('menuId', menuId);

  const response = await api.post('/upload/cloudinary/onboarding-modal-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Delete an onboarding modal image for a menu
 * @param menuId - The ID of the menu
 * @returns Promise with delete result
 */
export const deleteOnboardingModalImage = async (
  menuId: string
): Promise<OnboardingModalImageDeleteResponse> => {
  const response = await api.delete(`/upload/cloudinary/onboarding-modal-image/${menuId}`);
  return response.data;
};

/**
 * Upload a category modal top image for a menu
 * @param menuId - The ID of the menu
 * @param imageFile - The image file to upload
 * @returns Promise with upload result
 */
export const uploadCategoryTopImage = async (
  menuId: string,
  imageFile: File
): Promise<CategoryTopImageUploadResponse> => {
  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('menuId', menuId);

  const response = await api.post('/upload/cloudinary/category-top-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Delete a category modal top image for a menu
 * @param menuId - The ID of the menu
 * @returns Promise with delete result
 */
export const deleteCategoryTopImage = async (
  menuId: string
): Promise<CategoryTopImageDeleteResponse> => {
  const response = await api.delete(`/upload/cloudinary/category-top-image/${menuId}`);
  return response.data;
};

/**
 * Update a menu item to remove image references
 * @param menuItemId - The ID of the menu item
 * @returns Promise with update result
 */
export const removeMenuItemImageReferences = async (
  menuItemId: string
): Promise<any> => {
  const response = await api.put(`/menuItems/${menuItemId}`, {
    imageUrl: null,
    cloudinaryPublicId: null,
  });
  return response.data;
};

export default {
  uploadMenuItemImage,
  deleteMenuItemImage,
  removeMenuItemImageReferences,
  uploadMenuImage,
  deleteMenuImage,
  uploadOnboardingModalImage,
  deleteOnboardingModalImage,
  uploadCategoryTopImage,
  deleteCategoryTopImage,
};
